import classNames from 'classnames';
import useIsMinimumMapHeight from '@alltrails/shared/hooks/useIsMinimumMapHeight';
import MapSelectorModal from '@alltrails/maps/components/MapSelectorModal';
import useStyleKeyboardShortcut from '@alltrails/maps/hooks/useStyleKeyboardShortcut';
import { getMapLocation } from '@alltrails/maps/utils/amplitude';
import { additionalStyleCardConfigs, baseStyleCardConfigs } from '@alltrails/maps/utils/cardConfigs';
import getStyleCardConfigs from '@alltrails/maps/utils/getStyleCardConfigs';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import IconButtonGroup from '@alltrails/shared/denali/components/IconButtonGroup';
import Maps2d from '@alltrails/shared/icons/2d';
import Maps3d from '@alltrails/shared/icons/3d';
import Compass from '@alltrails/shared/icons/Compass';
import Layers from '@alltrails/shared/icons/Layers';
import useToggle from '@alltrails/shared/hooks/useToggle';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import { wrapUrlSafe } from 'utils/language_support_util';
import logThreeDMapButtonClicked from '@alltrails/analytics/events/logThreeDMapButtonClicked';
import logMapLayerButtonTapped from '@alltrails/analytics/events/logMapLayerButtonTapped';
import { AdditionalStyleId, StyleId } from '@alltrails/maps/types/Styles';
import { ComponentProps, MouseEvent } from 'react';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useExperiment from 'hooks/useExperiment';
import useUser from 'hooks/useUser';
import SignupModal from 'components/SignupModal';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import CheckoutModal from 'components/CheckoutModal';
import * as styles from './styles/styles.module.scss';
import * as printStyles from './styles/print.module.scss';

type Props = {
  adminCustomizationSettings?: ComponentProps<typeof MapSelectorModal>['adminCustomizationSettings'];
  adminLayout?: boolean;
  compassRotation?: number;
  currentLayerTerrainEnabled?: boolean;
  currentMapLayer: StyleId;
  enabledOverlays: AdditionalStyleId[];
  handleAdminCustomizationSettingsChange?: (settingsKey: string, sourceUrl?: string) => void;
  handleCompassClick?: (e: MouseEvent<HTMLElement>) => void;
  handleDisable3D?: () => void;
  handleEnable3D?: () => void;
  handleResetClick: ComponentProps<typeof MapSelectorModal>['handleResetClick'];
  isAuthenticated?: boolean;
  isNotExploreMap?: boolean;
  mobileBrowser: boolean;
  onActivityFilterChange: ComponentProps<typeof MapSelectorModal>['handleActivityFilterChange'];
  onCardClick: ComponentProps<typeof MapSelectorModal>['handleCardClick'];
  onHeatmapClick: ComponentProps<typeof MapSelectorModal>['handleHeatmapClick'];
  permissions: string[];
  selectedFilterActivity?: ComponentProps<typeof MapSelectorModal>['selectedFilterActivity'];
  shouldConditionallyRender?: boolean;
  terrainActive?: boolean;
  terrainDisabled?: boolean;
  trailId?: number;
};

const MapSelection = ({
  adminCustomizationSettings = {},
  adminLayout = false,
  compassRotation = 0,
  currentLayerTerrainEnabled = false,
  currentMapLayer,
  enabledOverlays,
  handleAdminCustomizationSettingsChange = () => {},
  handleCompassClick,
  handleDisable3D,
  handleEnable3D,
  handleResetClick,
  isAuthenticated = false,
  isNotExploreMap = true,
  mobileBrowser,
  onActivityFilterChange,
  onCardClick,
  onHeatmapClick,
  permissions,
  selectedFilterActivity = null,
  shouldConditionallyRender = false,
  terrainActive = false,
  terrainDisabled = false,
  trailId = -1
}: Props) => {
  const user = useUser();
  const experiment = useExperiment('growth-v2-on-page-web-checkout');
  const intl = useIntl();
  const isMinimumMapHeight = useIsMinimumMapHeight();
  const [open, toggle] = useToggle(false);
  const languageRegionCode = useLanguageRegionCode();
  const [isSignupModalOpen, toggleSignupModal] = useToggle();
  const [isCheckoutModalOpen, toggleCheckoutModal] = useToggle();

  let body: HTMLElement;
  let hideForFullscreen: HTMLElement;
  if (typeof window !== 'undefined') {
    body = document.querySelector('body');
    hideForFullscreen = document.querySelector('.hide-for-fullscreen-modal');
  }

  const closeModal = () => {
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'unset';
      hideForFullscreen.style.display = '';
    }
  };

  const openModal = () => {
    logMapLayerButtonTapped({ map_location: getMapLocation(window.location.pathname) });
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'hidden';
      hideForFullscreen.style.display = 'none';
    }
  };

  const getTerrainButton = () => {
    if (terrainDisabled || !currentLayerTerrainEnabled || !handleDisable3D || !handleEnable3D) {
      return null;
    }
    const onClick = () => {
      logThreeDMapButtonClicked({ map_location: getMapLocation(window.location.pathname), trail_id: trailId });

      if (experiment?.value === 'treatment') {
        if (!user) {
          toggleSignupModal();
        } else if (!user.pro) {
          toggleCheckoutModal();
        } else if (terrainActive) {
          handleDisable3D();
        } else {
          handleEnable3D();
        }
      } else if (terrainActive) {
        handleDisable3D();
      } else if (user?.pro) {
        handleEnable3D();
      } else {
        const params = new URLSearchParams();
        params.set('returnTo', window.location.toString());
        const path = `${isAuthenticated ? '/plus' : `/signup?${params.toString()}`}`;
        window.location.assign(wrapUrlSafe(path, languageRegionCode));
      }
    };
    const title = terrainActive ? intl.formatMessage({ defaultMessage: 'Disable 3D map' }) : intl.formatMessage({ defaultMessage: 'Enable 3D map' });
    return { icon: { Component: terrainActive ? Maps2d : Maps3d }, onClick, title, testId: title };
  };

  const { mapTypesCards, mapDetailsCards, additionalOptionsCards } = getStyleCardConfigs({
    currentMapLayer,
    enabledOverlays,
    isAdmin: adminLayout,
    additionalStyleCardConfigs,
    permissions,
    baseStyleCardConfigs,
    terrainActive
  });

  // Note that this is defined outside the scope of the cards modal. These
  // shortcuts are meant to be used without having to open that modal
  useStyleKeyboardShortcut(styleId => {
    const config = [...mapDetailsCards, ...mapTypesCards].find(({ key }) => key === styleId);
    if (!config) {
      return;
    }

    onCardClick(config);
  });

  const shouldRenderButtons = !shouldConditionallyRender || isMinimumMapHeight;

  return (
    <div className={classNames(styles.controls, printStyles.controls, { [styles.isNotExploreMap]: isNotExploreMap })}>
      <SignupModal
        heading={<FormattedMessage defaultMessage="Access 3D Maps and more{br}with AllTrails+" values={{ br: <br /> }} />}
        isOpen={isSignupModalOpen}
        onRequestClose={toggleSignupModal}
        onSuccess={user => {
          toggleSignupModal();

          if (!user.pro) {
            toggleCheckoutModal();
          }
        }}
        trigger={CarouselDisplayTrigger.ThreeDMapsTapped}
      />
      <CheckoutModal isOpen={isCheckoutModalOpen} onRequestClose={toggleCheckoutModal} trigger={CarouselDisplayTrigger.ThreeDMapsTapped} />
      {shouldRenderButtons && (
        <>
          <IconButtonGroup
            className={terrainActive ? '' : styles.brandColor3DButton}
            buttons={[
              {
                icon: { Component: Layers },
                onClick: openModal,
                title: intl.formatMessage({ defaultMessage: 'Map options' }),
                testId: 'map-options'
              },
              getTerrainButton()
            ].filter(button => !!button)}
            orientation="vertical"
            testId="map-selection-controls"
          />
          {terrainDisabled ? null : (
            <IconButton
              className={styles.compass}
              icon={{ Component: Compass, orientation: compassRotation }}
              onClick={handleCompassClick}
              testId="map-compass"
              title={intl.formatMessage({ defaultMessage: 'Reset north and pitch' })}
              variant="elevated"
            />
          )}
        </>
      )}
      {open && (
        <MapSelectorModal
          mapTypesCards={mapTypesCards}
          mapDetailsCards={mapDetailsCards}
          additionalOptionsCards={additionalOptionsCards}
          handleClose={closeModal}
          handleResetClick={handleResetClick}
          handleCardClick={onCardClick}
          handleHeatmapClick={onHeatmapClick}
          handleActivityFilterChange={onActivityFilterChange}
          selectedFilterActivity={selectedFilterActivity}
          adminCustomizationSettings={adminCustomizationSettings}
          handleAdminCustomizationSettingsChange={handleAdminCustomizationSettingsChange}
          adminLayout={adminLayout}
          mobileBrowser={mobileBrowser}
          enabledOverlays={enabledOverlays}
        />
      )}
    </div>
  );
};

export default MapSelection;
